import type { Address, Instruction, Transaction } from "@solana/kit";
import { isSwigTransaction, parseSwigTransaction, getTokenPayerFromTransaction } from "./utils";

export interface NormalizedTransaction {
  instructions: Array<{
    programAddress: Address;
    accounts: Array<{ address: Address; role: number }>;
    data: Uint8Array;
  }>;
  payer: string;
}

/**
 * Normalizes different SVM transaction shapes into a single facilitator-friendly
 * instruction list plus the effective payer that should be used for settlement.
 */
export interface TransactionNormalizer {
  /**
   * Returns true when this normalizer understands the provided instruction layout.
   *
   * @param instructions - Decompiled instruction list from the transaction.
   * @returns True when the implementation can normalize the transaction.
   */
  canHandle(instructions: ReadonlyArray<Instruction>): boolean;
  /**
   * Converts a transaction into the canonical shape expected by facilitator verification.
   *
   * @param instructions - Decompiled instruction list from the transaction.
   * @param staticAccounts - Ordered static account list from the compiled message.
   * @param transaction - Original decoded transaction.
   * @returns The normalized instruction set and payer.
   */
  normalize(
    instructions: ReadonlyArray<Instruction>,
    staticAccounts: ReadonlyArray<Address>,
    transaction: Transaction,
  ): Promise<NormalizedTransaction>;
}

/**
 * Handles Swig smart-wallet transactions by flattening the SignV2 payload into
 * the same normalized shape used for regular token transfers.
 */
class SwigNormalizer implements TransactionNormalizer {
  /**
   * Detects whether the instruction list matches the Swig SignV2 layout.
   *
   * @param instructions - Decompiled instruction list from the transaction.
   * @returns True when the transaction should be normalized as Swig.
   */
  canHandle(instructions: ReadonlyArray<Instruction>): boolean {
    return isSwigTransaction(instructions);
  }

  /**
   * Flattens the Swig SignV2 payload and uses the Swig PDA as the logical payer.
   *
   * @param instructions - Decompiled instruction list from the transaction.
   * @param staticAccounts - Ordered static account list from the compiled message.
   * @returns The normalized Swig instructions and payer.
   */
  async normalize(
    instructions: ReadonlyArray<Instruction>,
    staticAccounts: ReadonlyArray<Address>,
  ): Promise<NormalizedTransaction> {
    const result = await parseSwigTransaction(instructions, staticAccounts);
    return {
      instructions: result.instructions,
      payer: result.swigPda,
    };
  }
}

/**
 * Handles ordinary SVM transfers that do not require special wallet decoding.
 */
class RegularNormalizer implements TransactionNormalizer {
  /**
   * Acts as the fallback normalizer once specialized handlers decline the payload.
   *
   * @returns Always true because regular normalization is the default path.
   */
  canHandle(): boolean {
    return true;
  }

  /**
   * Passes through decompiled instructions and derives the transfer payer directly
   * from the decoded transaction.
   *
   * @param instructions - Decompiled instruction list from the transaction.
   * @param _staticAccounts - Unused static accounts for the regular path.
   * @param transaction - Original decoded transaction.
   * @returns The normalized instruction list and derived payer.
   */
  async normalize(
    instructions: ReadonlyArray<Instruction>,
    _staticAccounts: ReadonlyArray<Address>,
    transaction: Transaction,
  ): Promise<NormalizedTransaction> {
    const payer = getTokenPayerFromTransaction(transaction);
    if (!payer) {
      throw new Error("invalid_exact_svm_payload_no_transfer_instruction");
    }

    return {
      // The caller already decompiled the instructions; pass them through.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      instructions: instructions as any,
      payer,
    };
  }
}

const defaultNormalizers: TransactionNormalizer[] = [new SwigNormalizer(), new RegularNormalizer()];

/**
 * Chooses the first matching normalizer and converts the transaction into the
 * canonical instruction shape consumed by the facilitator verifier.
 *
 * @param instructions - Decompiled instruction list from the transaction.
 * @param staticAccounts - Ordered static account list from the compiled message.
 * @param transaction - Original decoded transaction.
 * @returns The normalized instruction list and payer.
 */
export async function normalizeTransaction(
  instructions: ReadonlyArray<Instruction>,
  staticAccounts: ReadonlyArray<Address>,
  transaction: Transaction,
): Promise<NormalizedTransaction> {
  for (const n of defaultNormalizers) {
    if (n.canHandle(instructions)) {
      return await n.normalize(instructions, staticAccounts, transaction);
    }
  }
  throw new Error("no normalizer found for transaction");
}
